import {RefObject, useCallback, useEffect, useRef, useState} from 'react';

interface ScrollHiddenState {
  isScrollingDown: boolean;
  isNearBottom: boolean;
  setIsScrollingDown: (value: boolean) => void;
}

const useScrolling = (
  containerRef: RefObject<HTMLElement>,
): ScrollHiddenState => {
  const lastScrollY = useRef(0);
  const lastTouchY = useRef(0);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const BOTTOM_OFFSET = 64;
  const SCROLL_THRESHOLD = 5;

  const handleScroll = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      const currentScrollY = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;

      if (scrollHeight <= clientHeight) return;

      const nearBottom =
        currentScrollY + clientHeight >= scrollHeight - BOTTOM_OFFSET;
      setIsNearBottom(nearBottom);

      if (nearBottom) {
        if (isScrollingDown) setIsScrollingDown(false);
      } else {
        const scrollDelta = currentScrollY - lastScrollY.current;

        if (Math.abs(scrollDelta) > SCROLL_THRESHOLD) {
          if (scrollDelta > 0) {
            setIsScrollingDown(true);
          } else {
            setIsScrollingDown(false);
          }
        }
      }

      lastScrollY.current = currentScrollY;
    },
    [isScrollingDown],
  );

  const handleTouchStart = useCallback((event: TouchEvent) => {
    lastTouchY.current = event.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    const currentTouchY = event.touches[0].clientY;
    const touchDelta = lastTouchY.current - currentTouchY;

    if (Math.abs(touchDelta) > SCROLL_THRESHOLD) {
      if (touchDelta > 0) {
        setIsScrollingDown(true);
      } else {
        setIsScrollingDown(false);
      }
    }

    lastTouchY.current = currentTouchY;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollableElements = new Set<HTMLElement>();

    const addScrollListener = (element: HTMLElement) => {
      if (!scrollableElements.has(element)) {
        element.addEventListener('scroll', handleScroll, {passive: true});
        element.addEventListener('touchstart', handleTouchStart, {
          passive: true,
        });
        element.addEventListener('touchmove', handleTouchMove, {passive: true});
        scrollableElements.add(element);
      }
    };

    const removeScrollListener = (element: HTMLElement) => {
      if (scrollableElements.has(element)) {
        element.removeEventListener('scroll', handleScroll);
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        scrollableElements.delete(element);
      }
    };

    // Check if an element is scrollable
    const isScrollable = (element: HTMLElement): boolean => {
      const style = window.getComputedStyle(element);

      // Check for iOS momentum scrolling support
      const hasIOSMomentumScroll =
        style.getPropertyValue('-webkit-overflow-scrolling') === 'touch' ||
        (style as any)['-webkit-overflow-scrolling'] === 'touch' ||
        // @ts-ignore
        element.style.webkitOverflowScrolling === 'touch';

      return (
        (style.overflowY === 'auto' ||
          style.overflowY === 'scroll' ||
          hasIOSMomentumScroll) &&
        element.scrollHeight > element.clientHeight
      );
    };

    // Find all scrollable elements recursively
    const findAllScrollable = (root: HTMLElement) => {
      if (isScrollable(root)) {
        addScrollListener(root);
      }

      const children = root.children;
      for (let i = 0; i < children.length; i++) {
        findAllScrollable(children[i] as HTMLElement);
      }
    };

    // Initial setup
    findAllScrollable(container);

    // Watch for DOM changes
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        // Handle added nodes
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            findAllScrollable(node as HTMLElement);
          }
        });

        // Handle attribute changes
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'style' ||
            mutation.attributeName === 'class')
        ) {
          const element = mutation.target as HTMLElement;
          if (isScrollable(element)) {
            addScrollListener(element);
          } else {
            removeScrollListener(element);
          }
        }
      });
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      scrollableElements.forEach(element => {
        removeScrollListener(element);
      });
      observer.disconnect();
    };
  }, [containerRef, handleScroll, handleTouchStart, handleTouchMove]);

  return {
    isScrollingDown,
    setIsScrollingDown,
    isNearBottom,
  };
};

export default useScrolling;

interface UseScrollOptions {
  bottomOffset?: number;
  threshold?: number;
}

interface UseScrollReturn {
  isScrollingDown: boolean;
  isScrollingUp: boolean;
  isNearBottom: boolean;
}

export const useScrollV2 = (
  containerRef: RefObject<HTMLElement>,
  options?: UseScrollOptions,
): UseScrollReturn => {
  const {bottomOffset = 120, threshold = 10} = options || {};

  const lastScrollY = useRef(0);

  const [isScrollingDown, setIsScrollingDown] = useState(false);

  const [isNearBottom, setIsNearBottom] = useState(false);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    let ticking = false;

    const updateScrollState = () => {
      const currentScrollY = container.scrollTop;

      const delta = currentScrollY - lastScrollY.current;

      // direction
      if (Math.abs(delta) > threshold) {
        const scrollingDown = delta > 0;

        setIsScrollingDown(prev => {
          if (prev !== scrollingDown) {
            return scrollingDown;
          }

          return prev;
        });
      }

      // near bottom
      const nearBottom =
        currentScrollY + container.clientHeight >=
        container.scrollHeight - bottomOffset;

      setIsNearBottom(prev => {
        if (prev !== nearBottom) {
          return nearBottom;
        }

        return prev;
      });

      lastScrollY.current = currentScrollY;

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [bottomOffset, threshold, containerRef]);

  return {
    isScrollingDown,
    isScrollingUp: !isScrollingDown,
    isNearBottom,
  };
};
